/// <reference path="../../../Core/DataTypes/types.js" />
import ImageGallery from './ImageGallery';
import LevelSelection from './LevelSelection';
import { useTranslation } from 'react-i18next';
import { useGlobal } from '../../../Core/GlobalState/Store';
import Currencies from '../../../Core/Enums/Currencies';
import { addVat } from '../../../Core/Services/PaymentService';
import SettingsService from '../../../Core/Services/SettingsService';

export default function ContainerInfoComponent({
  roomList,
  selectedSize,
  selectedLevel,
  setSelectedLevel,
  handleNext,
  onClose,
}) {
  const { t, i18n } = useTranslation();
  const { setRoom, currentLocation } = useGlobal();

  function goToRequestBooking() {
    const size = selectedSize?.formSize ?? '';
    const container = currentLocation?.page_id ?? '';
    const formPage = SettingsService.getFormPage();
    window.location.href = `${window.location.origin}/${formPage}/?size=${size}&type=self_storage&container=${container}`;
  }

  async function setHighestDwellTimeRoom() {
    if (SettingsService.isOnlineBookingClosed()) {
      goToRequestBooking();
      return;
    }

    const filteredRooms = roomList.filter(
      room =>
        selectedSize.sizeTypes.includes(room.ItemSizeType.Name) &&
        room.IsIndoor === selectedSize.isIndoor &&
        room.level === selectedLevel
    );

    if (filteredRooms.length === 0) return;

    const highestDwellTimeRoom = filteredRooms.reduce((prev, current) => {
      const prevDwellTime = prev.DateFrom
        ? new Date(prev.DateFrom) - new Date()
        : -Infinity;

      const currentDwellTime = current.DateFrom
        ? new Date(current.DateFrom) - new Date()
        : -Infinity;
      return prevDwellTime < currentDwellTime ? prev : current;
    });

    await setRoom(highestDwellTimeRoom);
    handleNext();
  }

  return (
    <div className="w-full">
      <div className="!border !border-solid border-gray-300">
        <div className="">
          <div className="mt-auto flex justify-between">
            <ImageGallery
              images={selectedSize.images || []}
              altText={selectedSize.title}
            />
          </div>
        </div>
        <div className="p-8 flex flex-col gap-6 !border-t !border-gray-400 !border-1">
          <div className="font-bold text-xl flex justify-between items-center gap-4">
            <div>{selectedSize && selectedSize.title}</div>
            <div>
              {selectedSize &&
                selectedSize.availableAmount > 0 &&
                t('container_from_price', {
                  price: addVat(selectedSize.fromPrice).toLocaleString(
                    i18n.language,
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }
                  ),
                  currency: Currencies.getCodeByValue(selectedSize.currency),
                })}
            </div>
          </div>
          <div className="text-base">
            {selectedSize && selectedSize.description}
          </div>
          {selectedSize && selectedSize.availableAmount > 0 && (
            <div>
              <div className="flex justify-between items-center">
                <div className="flex gap-4 flex-wrap">
                  <div className="text-sm font-normal !border !border-solid border-gray-300 px-2 py-1">
                    {t('length_letter')}: {selectedSize && selectedSize.length}{' '}
                    m
                  </div>
                  <div className="text-sm font-normal !border !border-solid border-gray-300 px-2 py-1">
                    {t('width_letter')}: {selectedSize && selectedSize.width} m
                  </div>
                  <div className="text-sm font-normal !border !border-solid border-gray-300 px-2 py-1">
                    {t('height_letter')}: {selectedSize && selectedSize.height}{' '}
                    m
                  </div>
                  <div className="text-sm font-normal !border !border-solid border-gray-300 px-2 py-1">
                    {selectedSize &&
                      (selectedSize.length * selectedSize.width).toFixed(
                        2
                      )}{' '}
                    m²
                  </div>
                </div>
                <div className="text-red-300 text-sm">
                  {selectedSize &&
                    selectedSize.availableAmount > 0 &&
                    selectedSize.availableAmount + ' ' + t('units_available')}
                </div>
              </div>
            </div>
          )}
          <div>
            {selectedSize && selectedSize.availableAmount > 0 && (
              <LevelSelection
                selectedSize={selectedSize}
                selectedLevel={selectedLevel}
                setSelectedLevel={setSelectedLevel}
              />
            )}
            {selectedSize && selectedSize.availableAmount < 1 && (
              <div className="bg-[#F2F5F8] !border !border-solid border-gray-300 p-6 mt-6 text-center">
                <div className="font-bold text-sm">
                  {t('sorry_for_inconvenience')}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {t('send_a_request')}
                </div>
              </div>
            )}
            <div className="flex w-full">
              {onClose && (
                <div
                  onClick={onClose}
                  className="w-1/2 bg-gray-200 h-[60px] align-center flex justify-center items-center hover:cursor-pointer hover:bg-gray-300"
                >
                  <div className="text-gray-700 font-bold">{t('close')}</div>
                </div>
              )}
              {selectedSize && selectedSize.availableAmount == 0 ? (
                <div
                  onClick={goToRequestBooking}
                  className={`${onClose ? 'w-1/2' : 'w-full'} bg-primaryblue h-[60px] align-center flex justify-center items-center hover:cursor-pointer hover:bg-primaryorange`}
                >
                  <div className="text-white font-bold">
                    {t('request_booking')}
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => setHighestDwellTimeRoom()}
                  className={`${onClose ? 'w-1/2' : 'w-full'} bg-primaryblue h-[60px] align-center flex justify-center items-center hover:cursor-pointer hover:bg-primaryorange`}
                >
                  <div className="text-white font-bold">{t('choose')}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
